/**
 * /dashboard/leave — Leave module hub, "My requests" view (SOP §1.2;
 * Phase 3b — 2026-06-01).
 *
 * The old Next.js page intercepted legacy `?tab=balance|approvals`
 * deep-links with a server-side redirect; in the Vite SPA every entry
 * point goes through the typed router so the legacy-tab redirect helper
 * is dropped. Anyone bookmarking the old query-string surface lands on
 * the My Requests view, which is the original default anyway.
 *
 * Body is `<LeaveRequestList mode="self">` + a top-right "Request leave"
 * CTA. PageHeader + Time & Leave strip come from the shared
 * TimeAndLeaveShell — same DRY pattern as Calendar / Approvals (Phase
 * 3a).
 *
 * Migration note: port of frontend/src/app/(dashboard)/dashboard/leave/page.tsx
 * (server component, legacy `?tab=` redirect dropped) +
 * frontend/src/components/leave/leave-requests-tab.tsx (CTA + list)
 * flattened into one client component since the redirect step is gone.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { CalendarPlus } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { LeaveBalanceSummary } from "@/components/leave/leave-balance-summary";
import { LeaveRequestList } from "@/components/leave/leave-request-list";
import { UpcomingHolidaysCard } from "@/components/leave/upcoming-holidays-card";
import { Button } from "@/components/ui/button";
import { leaveKeys } from "@/hooks/use-leave";
import { leavePagesQueryOptions } from "@/hooks/use-leave-request";
import { leaveApi } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/leave/")({
  // A9 hover-prefetch. Primary = the self-view request list —
  // LeaveRequestList mode="self" starts on status "all" and fetches with
  // mine=1 (ONLY the caller's own rows, so an admin whose natural SQL
  // scope is tenant-wide still sees exactly their own history), whose
  // cache key is the canonical requests bucket + "mine" + the "scoped"
  // discriminator (see leave-request-list.tsx). Balance summary (the
  // server-resolved CURRENT leave-year — no year param, same key as
  // LeaveBalanceSummary) + the leave-type catalogue are secondary →
  // fire-and-forget, never awaited.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: leaveKeys.types(),
      queryFn: ({ signal }) => leaveApi.listLeaveTypes(signal),
    });
    void context.queryClient.prefetchQuery({
      queryKey: leaveKeys.balance(),
      queryFn: ({ signal }) => leaveApi.getLeaveBalance({}, signal),
    });
    // First page of the paged list (wave 2) — same key as
    // LeaveRequestList's useLeaveRequestPages.
    return context.queryClient
      .ensureInfiniteQueryData(leavePagesQueryOptions({ mine: true }, "mine"))
      .catch(() => undefined);
  },
  component: LeaveHubRoute,
});

function LeaveHubRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  return (
    <TimeAndLeaveShell
      title="Requests"
      description="Your leave requests, balances and approvals."
      actions={
        <Button asChild className="gap-1.5">
          <Link to="/app/$tenantSlug/leave/new" params={{ tenantSlug }}>
            <CalendarPlus className="h-4 w-4" />
            Request Leave
          </Link>
        </Button>
      }
    >
      <LeaveFeatureGate>
        <div className="space-y-6">
          <LeaveBalanceSummary />
          {/* Optional holidays are picked here, on the Me surface (QA
              2026-10 leave #8) — /holidays is the Run-side admin page. */}
          <UpcomingHolidaysCard />
          <LeaveRequestList mode="self" />
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
